import { useCallback, useEffect, useState } from 'react';
import type { HighlightRange, MarkKind } from '@/features/quiz/utils/highlightRanges';
import { subtractRange, toggleRange } from '@/features/quiz/utils/highlightRanges';

// v1 is a study aid, not data we owe the backend — sessionStorage keeps a
// user's marks for a question alive across the quiz session (including
// stepping back via flag-for-review) without writing anything server-side,
// and clears itself out when the tab/session ends.
const STORAGE_PREFIX: Record<MarkKind, string> = {
  highlight: 'swtpa:question-highlights:v1:',
  strike: 'swtpa:question-strikes:v1:',
};

function storageKey(questionId: string, kind: MarkKind): string {
  return `${STORAGE_PREFIX[kind]}${questionId}`;
}

function load(questionId: string | null | undefined, kind: MarkKind): HighlightRange[] {
  if (!questionId || typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(storageKey(questionId, kind));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is HighlightRange =>
        r && typeof r.start === 'number' && typeof r.end === 'number' && r.start < r.end,
    );
  } catch {
    // Corrupt entry or storage unavailable (Safari private mode, quota, etc.)
    // — a lost set of highlights is a non-event; never let it crash the quiz.
    return [];
  }
}

function save(questionId: string, kind: MarkKind, ranges: HighlightRange[]): void {
  if (typeof window === 'undefined') return;
  try {
    if (ranges.length === 0) {
      window.sessionStorage.removeItem(storageKey(questionId, kind));
    } else {
      window.sessionStorage.setItem(storageKey(questionId, kind), JSON.stringify(ranges));
    }
  } catch {
    // Same reasoning as load() — highlighting is best-effort, never fatal.
  }
}

/**
 * Per-question, session-persisted state for ONE mark kind. One user gesture,
 * `toggle(selection)`, drives both adding and removing marks (see
 * `toggleRange`'s doc comment for the exact rule); `clear(selection)` is the
 * unconditional removal a caller needs to keep the two kinds exclusive over
 * the same span.
 */
export function useQuestionHighlights(
  questionId: string | null | undefined,
  kind: MarkKind = 'highlight',
) {
  const [ranges, setRanges] = useState<HighlightRange[]>(() => load(questionId, kind));

  // Re-hydrate whenever the question changes (new question, or navigating
  // back to a previously-marked one via flag-for-review / retry).
  useEffect(() => {
    setRanges(load(questionId, kind));
  }, [questionId, kind]);

  const toggle = useCallback(
    (selection: HighlightRange) => {
      if (!questionId) return;
      setRanges((prev) => {
        const next = toggleRange(prev, selection);
        save(questionId, kind, next);
        return next;
      });
    },
    [questionId, kind],
  );

  const clear = useCallback(
    (selection: HighlightRange) => {
      if (!questionId) return;
      setRanges((prev) => {
        const next = subtractRange(prev, selection);
        save(questionId, kind, next);
        return next;
      });
    },
    [questionId, kind],
  );

  return { ranges, toggle, clear };
}
